import React, { useCallback, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { getUA } from 'core/singleton'
import { layoutSmall } from 'core/util/default'

interface SectionContentProps {
  className?: string
}

const SectionContent: React.FC<SectionContentProps> = (props) => {
  const { className } = props
  const sectionContentEl = useRef<HTMLDivElement>(null)
  const isDown = useRef<boolean>(false)
  const posX = useRef<number>(0)
  const { isSDK } = getUA()

  const handleMouseDown = useCallback((e) => {
    if (sectionContentEl.current) {
      isDown.current = true
      posX.current = e.clientX
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    isDown.current = false
  }, [])

  const handleMouseMove = useCallback((e) => {
    if (isDown.current && sectionContentEl.current) {
      sectionContentEl.current.scrollLeft -= (e.clientX - posX.current) / 10
    }
  }, [])

  const handleTouchStart = useCallback((e: TouchEvent) => {
    e.stopPropagation()
  }, [])

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    e.stopPropagation()
  }, [])

  const handleCleanUp = useCallback(() => {
    if (!sectionContentEl.current || isSDK) return
    sectionContentEl.current.removeEventListener('touchstart', handleTouchStart)
    sectionContentEl.current.removeEventListener('touchend', handleTouchEnd)
  }, [handleTouchEnd, handleTouchStart, isSDK])

  useEffect(() => {
    if (sectionContentEl.current) {
      sectionContentEl.current.addEventListener('mouseleave', handleMouseLeave)
      sectionContentEl.current.addEventListener('mouseup', handleMouseLeave)
      sectionContentEl.current.addEventListener('mousedown', handleMouseDown)
      sectionContentEl.current.addEventListener('mousemove', handleMouseMove)
      if (!isSDK) {
        sectionContentEl.current.addEventListener('touchstart', handleTouchStart)
        sectionContentEl.current.addEventListener('touchend', handleTouchEnd)
      }
    }
    return () => handleCleanUp()
  }, [
    isSDK,
    handleMouseMove,
    handleMouseDown,
    handleMouseLeave,
    handleTouchStart,
    handleTouchEnd,
    handleCleanUp,
  ])

  return (
    <StyledSectionContent className={className} ref={sectionContentEl} needsOverflow={!isSDK}>
      {props.children}
    </StyledSectionContent>
  )
}

const StyledSectionContent = styled.div<{ needsOverflow?: boolean }>`
  ${(needsOverflow) =>
    needsOverflow &&
    css`
      overflow: auto;
    `}
  display: flex;
  align-items: flex-start;
  padding-top: 16px;
  padding-bottom: 36px;
  cursor: grab;
  scroll-behavior: unset !important;
  ::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: ${({ theme }) => theme.border};
  }

  > div {
    padding-left: 12px;
  }
  > a,
  > button {
    padding-left: 24px;
  }
  > div:first-child,
  > a:first-child,
  > button:first-child {
    padding-left: 20px;
  }
  > div:last-child,
  > a:last-child,
  > button:last-child {
    padding-right: 20px;
  }

  &.list {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    max-height: 529px;
    > div,
    > div:first-child {
      padding-left: 20px;
      padding-bottom: 20px;
    }
  }

  ${layoutSmall(css`
    > div {
      padding-left: 12px;
    }
    > a,
    > button {
      padding-left: 22px;
    }
    > div:first-child,
    > a:first-child,
    > button:first-child {
      padding-left: 16px;
    }
    > div:last-child,
    > a:last-child,
    > button:last-child {
      padding-right: 16px;
    }

    &.list {
      max-height: 426px;
      > div:first-child,
      > div {
        padding-left: 12px;
        padding-bottom: 16px;
      }
    }
  `)}

  &.horizontal {
    flex-wrap: nowrap;
  }
  &.vertical {
    flex-wrap: wrap;
    column-gap: 16px;
    row-gap: 20px;
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 0;

    > div {
      padding-left: 0;
      padding-right: 0;

      .card_wrapper {
        width: 100%;
        height: unset;
        padding-top: 134%;
      }
      .card_info {
        width: 100%;
      }
    }

    @media (max-width: 517px) {
      > div {
        width: calc((100% / 2) - (16px / 2));
      }
    }
    @media (min-width: 518px) and (max-width: 768px) {
      > div {
        width: calc((100% / 3) - (2 * 16px / 3));
      }
    }
    @media (min-width: 769px) and (max-width: 980px) {
      > div {
        width: calc((100% / 4) - (3 * 16px / 4));
      }
    }
    @media (min-width: 981px) and (max-width: 1200px) {
      > div {
        width: calc((100% / 5) - (4 * 16px / 5));
      }
    }
    @media (min-width: 1201px) {
      > div {
        width: calc((100% / 6) - (5 * 16px / 6));
      }
    }
  }
`
export default SectionContent
